import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, ArrowClockwise, X } from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "new", label: "New", tone: "bg-slate-100 text-slate-700" },
  { value: "processing", label: "Processing", tone: "bg-amber-50 text-amber-700 border border-amber-200" },
  { value: "paid", label: "Paid", tone: "bg-cyan-50 text-cyan-700 border border-cyan-200" },
  { value: "shipped", label: "Shipped", tone: "bg-blue-50 text-blue-700 border border-blue-200" },
  { value: "delivered", label: "Delivered", tone: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  { value: "cancelled", label: "Cancelled", tone: "bg-red-50 text-red-700 border border-red-200" },
  { value: "refunded", label: "Refunded", tone: "bg-purple-50 text-purple-700 border border-purple-200" },
];

const toneFor = (status) =>
  STATUS_OPTIONS.find((s) => s.value === (status || "").toLowerCase())?.tone ||
  "bg-slate-100 text-slate-700";

const fmtInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) toast.error(error.message);
    setOrders(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase();
    return orders.filter((o) => {
      const status = (o.order_status || o.status || "").toLowerCase();
      if (filter !== "all" && status !== filter) return false;
      if (!query) return true;
      const hay = `${o.id || ""} ${o.order_number || ""} ${o.email || ""} ${o.customer_email || ""} ${o.name || ""}`.toLowerCase();
      return hay.includes(query);
    });
  }, [orders, q, filter]);

  const updateStatus = async (order, newStatus) => {
    setSavingId(order.id);
    // Try both column names for maximum compatibility
    let res = await supabase.from("orders").update({ order_status: newStatus }).eq("id", order.id);
    if (res.error) {
      res = await supabase.from("orders").update({ status: newStatus }).eq("id", order.id);
    }
    if (res.error) {
      toast.error(res.error.message);
    } else {
      toast.success(`Order marked ${newStatus}`);
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, order_status: newStatus, status: newStatus } : o)),
      );
      if (selected?.id === order.id) setSelected({ ...selected, order_status: newStatus, status: newStatus });
    }
    setSavingId(null);
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const header = ["id", "order_number", "email", "total", "status", "created_at"];
    const csv = [
      header.join(","),
      ...rows.map((o) =>
        [
          o.id,
          o.order_number || "",
          o.email || o.customer_email || "",
          o.total || o.amount || 0,
          o.order_status || o.status || "new",
          o.created_at || "",
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="Orders">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-5">
        <div className="flex-1 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              data-testid="orders-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by ID, email, name…"
              className="pl-9 h-10 rounded-xl bg-white"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="orders-filter" className="w-44 h-10 rounded-xl bg-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={load}
            data-testid="orders-refresh"
            className="rounded-xl h-10 border-slate-200"
          >
            <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
            Refresh
          </Button>
          <Button
            onClick={exportCsv}
            data-testid="orders-export"
            className="rounded-xl h-10 bg-navy-deep hover:bg-navy text-white"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No orders match your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-mono-data">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Order</th>
                  <th className="text-left px-5 py-3 font-semibold">Customer</th>
                  <th className="text-left px-5 py-3 font-semibold">Date</th>
                  <th className="text-left px-5 py-3 font-semibold">Total</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const status = (o.order_status || o.status || "new").toLowerCase();
                  return (
                    <tr
                      key={o.id}
                      data-testid={`order-row-${o.id}`}
                      className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors"
                    >
                      <td className="px-5 py-3 font-mono-data text-[12px] text-navy-deep">
                        #{(o.order_number || o.id || "").toString().slice(0, 8)}
                      </td>
                      <td className="px-5 py-3 text-navy-deep">
                        {o.email || o.customer_email || o.name || "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-[12px]">{fmtDate(o.created_at)}</td>
                      <td className="px-5 py-3 font-semibold text-navy-deep tabular-nums">
                        {fmtInr(o.total || o.amount)}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-2 py-1 rounded-full ${toneFor(status)}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelected(o)}
                          data-testid={`order-view-${o.id}`}
                          className="h-8 rounded-lg text-[12px] border-slate-200"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-40 bg-navy-deep/40 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="order-detail-drawer"
          >
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-mono-data text-cyan-brand font-semibold">
                  Order
                </div>
                <h3 className="font-display font-bold text-navy-deep">
                  #{(selected.order_number || selected.id || "").toString().slice(0, 8)}
                </h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="h-9 w-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"
                aria-label="Close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data">
                    Customer
                  </div>
                  <div className="mt-1 text-navy-deep">{selected.email || selected.customer_email || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data">Total</div>
                  <div className="mt-1 font-mono-data font-bold text-navy-deep text-lg tabular-nums">
                    {fmtInr(selected.total || selected.amount)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data">Placed</div>
                  <div className="mt-1 text-navy-deep text-[13px]">{fmtDate(selected.created_at)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data">
                    Payment ID
                  </div>
                  <div className="mt-1 font-mono-data text-[12px] text-navy-deep truncate">
                    {selected.payment_id || selected.razorpay_payment_id || "—"}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data mb-2">
                  Update status
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => {
                    const active = (selected.order_status || selected.status || "").toLowerCase() === s.value;
                    return (
                      <button
                        key={s.value}
                        onClick={() => updateStatus(selected, s.value)}
                        disabled={savingId === selected.id}
                        data-testid={`status-${s.value}`}
                        className={`text-[11.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-3 py-1.5 rounded-full transition-all ${
                          active
                            ? `${s.tone} ring-2 ring-offset-1 ring-cyan-brand`
                            : `${s.tone} opacity-70 hover:opacity-100`
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selected.items && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data mb-2">Items</div>
                  <pre className="text-[12px] bg-slate-50 rounded-xl p-3 overflow-x-auto border border-slate-200">
                    {typeof selected.items === "string"
                      ? selected.items
                      : JSON.stringify(selected.items, null, 2)}
                  </pre>
                </div>
              )}

              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data mb-2">Raw</div>
                <pre className="text-[11px] bg-slate-50 rounded-xl p-3 overflow-x-auto border border-slate-200 max-h-72">
                  {JSON.stringify(selected, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
